import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Edit, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { partnerAdminKeys } from "@/constant/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { apiRequestV2, parseNormalizedError, queryClient } from "@/lib/queryClient";
import { getTokenListOptions, uploadAdminImage } from "@/services/queryOptions/partnerManagementQueryOptions";
import type { OuraListParams } from "@/types/oura";
import type { PartnerTokenListItem } from "@/types/partner-management";
import { AdminDataTable } from "../components/AdminDataTable";

type TokenFormValues = {
  name: string;
  tokenName: string;
  tokenMint: string;
  decimals: string;
  image: string;
};

const emptyForm: TokenFormValues = {
  name: "",
  tokenName: "",
  tokenMint: "",
  decimals: "9",
  image: "",
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function uploadFolder(...parts: Array<string | number | null | undefined>) {
  return parts
    .map((part) => slugify(String(part ?? "")))
    .filter(Boolean)
    .join("/");
}

function sameValue(current: unknown, previous: unknown) {
  return String(current ?? "") === String(previous ?? "");
}

function blockerDescription(details: unknown) {
  if (!details || typeof details !== "object") return "";
  return Object.entries(details as Record<string, number>)
    .filter(([, count]) => Number(count) > 0)
    .map(([key, count]) => `${key}: ${count}`)
    .join(", ");
}

function TokenImageField({
  value,
  file,
  onChange,
  onFileChange,
}: {
  value: string;
  file: File | null;
  onChange: (value: string) => void;
  onFileChange: (file: File | null) => void;
}) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);
    onChange(`https://local-upload.tweetonium/${encodeURIComponent(file.name)}`);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [file]);

  const preview = previewUrl || value;

  return (
    <div className="space-y-2 sm:col-span-2">
      <Label>Image</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(event) => {
            onFileChange(null);
            onChange(event.target.value);
          }}
          placeholder="https://assets.example.com/path/image.png"
        />
        <label className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-input px-3 text-sm">
          <Upload className="mr-2 h-4 w-4" />
          Choose
          <input
            className="hidden"
            type="file"
            accept=".png,.avif,.jpg,.jpeg,.webp"
            onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      {file && <div className="text-xs text-zinc-400">Selected: {file.name}</div>}
      {preview && <img src={preview} alt="" className="h-16 w-16 rounded border border-zinc-800 object-cover" />}
    </div>
  );
}

function TokenDialog({
  open,
  token,
  onOpenChange,
}: {
  open: boolean;
  token?: PartnerTokenListItem | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [form, setForm] = useState<TokenFormValues>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);

  useEffect(() => {
    setForm(token ? {
      name: token.name,
      tokenName: token.tokenName,
      tokenMint: token.tokenMint,
      decimals: String(token.decimals),
      image: token.image ?? "",
    } : emptyForm);
    setImageFile(null);
  }, [token, open]);

  const mutation = useMutation({
    mutationFn: async () => {
      const uploadedImage = imageFile
        ? (await uploadAdminImage(
            imageFile,
            "token",
            uploadFolder(form.tokenName || form.name),
            "token-image",
          )).objectName
        : undefined;
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!token || changed) payload[key] = value;
      };

      addField("name", form.name.trim(), token ? !sameValue(form.name.trim(), token.name) : true);
      addField("tokenName", form.tokenName.trim(), token ? !sameValue(form.tokenName.trim(), token.tokenName) : true);
      addField("tokenMint", form.tokenMint.trim(), token ? !sameValue(form.tokenMint.trim(), token.tokenMint) : true);
      addField("decimals", Number(form.decimals || 9), token ? Number(form.decimals || 9) !== token.decimals : true);

      if (uploadedImage !== undefined || (!token && form.image.trim())) {
        payload.image = uploadedImage ?? form.image.trim();
      } else if (token && !sameValue(form.image.trim(), token.image)) {
        payload.image = form.image.trim() || null;
      }

      if (token && Object.keys(payload).length === 0) return token;

      return apiRequestV2(
        token ? "PATCH" : "POST",
        token ? `/api/v1/token-list/${token.id}` : "/api/v1/token-list",
        payload,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.tokenList });
      toast({ title: token ? "Token updated" : "Token created" });
      onOpenChange(false);
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = blockerDescription(normalized.details);
      toast({
        title: token ? "Cannot update token" : "Cannot create token",
        description: details ? `${normalized.message} (${details})` : normalized.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{token ? "Edit Token" : "Add Token"}</DialogTitle>
          <DialogDescription>Manage token metadata used by Oura and partner configuration.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="USD Coin" />
          </div>
          <div className="space-y-2">
            <Label>Symbol</Label>
            <Input value={form.tokenName} onChange={(event) => setForm((current) => ({ ...current, tokenName: event.target.value }))} placeholder="USDC" />
          </div>
          <div className="space-y-2">
            <Label>Token Mint</Label>
            <Input value={form.tokenMint} onChange={(event) => setForm((current) => ({ ...current, tokenMint: event.target.value }))} placeholder="So11111111111111111111111111111111111111112" />
          </div>
          <div className="space-y-2">
            <Label>Decimals</Label>
            <Input value={form.decimals} onChange={(event) => setForm((current) => ({ ...current, decimals: event.target.value }))} placeholder="9" />
          </div>
          <TokenImageField
            value={form.image}
            file={imageFile}
            onChange={(image) => setForm((current) => ({ ...current, image }))}
            onFileChange={setImageFile}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !form.name.trim() || !form.tokenName.trim() || !form.tokenMint.trim()}
          >
            {mutation.isPending ? "Saving..." : token ? "Update Token" : "Create Token"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TokenManager() {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [dialogToken, setDialogToken] = useState<PartnerTokenListItem | null | undefined>();
  const tokens = useQuery(getTokenListOptions());
  const query = params.search?.trim().toLowerCase();
  const rows = (tokens.data ?? []).filter((token) => !query || [
    token.name,
    token.tokenName,
    token.tokenMint,
  ].some((value) => value.toLowerCase().includes(query)));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/token-list/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.tokenList });
      toast({ title: "Token deleted" });
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = blockerDescription(normalized.details);
      toast({
        title: "Cannot delete token",
        description: details ? `${normalized.message} (${details})` : normalized.message,
        variant: "destructive",
      });
    },
  });
  const columns = useMemo<ColumnDef<PartnerTokenListItem>[]>(() => [
    {
      accessorKey: "image",
      header: "Image",
      cell: ({ row }) => row.original.image ? <img src={row.original.image} alt="" className="h-10 w-10 rounded object-cover" /> : "-",
    },
    {
      accessorKey: "tokenName",
      header: "Symbol",
      cell: ({ row }) => <Badge variant="outline">{row.original.tokenName}</Badge>,
    },
    { accessorKey: "name", header: "Name" },
    {
      accessorKey: "tokenMint",
      header: "Mint",
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.tokenMint}</span>,
    },
    { accessorKey: "decimals", header: "Decimals" },
    {
      accessorKey: "updatedAt",
      header: "Updated",
      cell: ({ row }) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleString() : "-",
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setDialogToken(row.original)} aria-label="Edit token">
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (!window.confirm("Delete this token? This action cannot be undone.")) return;
              del.mutate(row.original.id);
            }}
            aria-label="Delete token"
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ], [del]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Tokens</h2>
        <p className="text-sm text-zinc-400">Manage token metadata used across Oura projects, partners, and collection minting.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Token List</CardTitle>
          <Button onClick={() => setDialogToken(null)}><Plus className="mr-2 h-4 w-4" />Add Token</Button>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={columns}
            data={rows}
            total={rows.length}
            params={params}
            onParamsChange={setParams}
            exportName="oura-tokens.csv"
            searchPlaceholder="Search tokens..."
          />
        </CardContent>
      </Card>
      <TokenDialog
        open={dialogToken !== undefined}
        token={dialogToken || null}
        onOpenChange={(open) => !open && setDialogToken(undefined)}
      />
    </div>
  );
}
